/**
 * Which operational layers the ICS-201's Box 4 map draws.
 *
 * Deliberately its own list rather than the Layers tab's tree. That panel
 * toggles the main map through `UscgMapContext`, and Box 4 needs choices that
 * belong to the printed sketch alone: turning a layer on for the form should
 * not change what the responder is looking at on the main map, or the other way
 * round. The categories and the scoping rule are shared with that panel though,
 * so the same layers show up under the same headings in both places.
 */

import React, { useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import {
  isExternalLayer,
  isLayerItemInCurrentScope,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isLayerItemInCurrentScope';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';

export type PickableLayer = {
  id: string;
  name: string;
  kind: 'internal' | 'external';
};

/** A folder from the Layers tab, or the catch-all for layers filed under none. */
export type PickableLayerGroup = {
  id: string;
  name: string;
  layers: PickableLayer[];
};

const UNCATEGORIZED_ID = '__uncategorized__';
const UNCATEGORIZED_NAME = 'Uncategorized';

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name);

/** Every layer available to this incident, kept under the folder it is filed in. */
export const usePickableLayers = (incidentId: string | undefined) => {
  const groupGuid = useGroupGuid();

  const { data, isLoading } = useListLayerNodes(
    {
      filter: {
        groupGuid: groupGuid ?? '',
        ...(incidentId ? { incidentIds: [incidentId] } : {}),
      },
    },
    { enabled: !!groupGuid }
  );

  const groups = useMemo<PickableLayerGroup[]>(() => {
    const folders: PickableLayerGroup[] = [];
    const uncategorized: PickableLayer[] = [];

    for (const group of data?.data ?? []) {
      const seen = new Set<string>();
      const layers: PickableLayer[] = [];

      for (const item of group.items) {
        if (!isLayerItemInCurrentScope(item, incidentId ?? null)) continue;
        const id = String(item.layer.id);
        // A layer filed twice under one folder comes back once per node.
        if (seen.has(id)) continue;
        seen.add(id);
        layers.push({
          id,
          name: item.node?.name || item.layer.name,
          kind: isExternalLayer(item.layer) ? 'external' : 'internal',
        });
      }

      if (layers.length === 0) continue;

      if (group.folder == null) {
        uncategorized.push(...layers);
      } else {
        folders.push({
          id: String(group.folder.id),
          name: group.folder.name,
          layers: layers.sort(byName),
        });
      }
    }

    folders.sort(byName);

    // Last, so the named categories are what the eye lands on first.
    if (uncategorized.length > 0) {
      folders.push({
        id: UNCATEGORIZED_ID,
        name: UNCATEGORIZED_NAME,
        layers: uncategorized.sort(byName),
      });
    }

    return folders;
  }, [data, incidentId]);

  /**
   * The same layer can be filed under more than one folder, so the flat list
   * the map sync works from is deduplicated across groups.
   */
  const layers = useMemo<PickableLayer[]>(() => {
    const seen = new Set<string>();
    return groups
      .flatMap((group) => group.layers)
      .filter((layer) => {
        if (seen.has(layer.id)) return false;
        seen.add(layer.id);
        return true;
      });
  }, [groups]);

  return { groups, layers, isLoading };
};

const LayerCategory: React.FC<{
  group: PickableLayerGroup;
  selectedIds: ReadonlySet<string>;
  onToggle: (layer: PickableLayer) => void;
  disabled?: boolean;
}> = ({ group, selectedIds, onToggle, disabled }) => (
  <Collapsible defaultOpen>
    <CollapsibleTrigger
      className="tw:group tw:flex tw:w-full tw:items-center tw:gap-1 tw:text-left tw:text-xs tw:font-semibold tw:text-muted-foreground tw:uppercase"
      data-testid={`ics201-map-layer-category-${group.id}`}
    >
      <ChevronRight className="tw:size-3 tw:shrink-0 tw:transition-transform tw:group-data-[panel-open]:rotate-90" />
      <span className="tw:min-w-0 tw:truncate">{group.name}</span>
      <span className="tw:ms-auto tw:shrink-0 tw:tabular-nums">
        {group.layers.length}
      </span>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div className="tw:mt-1 tw:flex tw:flex-col tw:gap-2 tw:ps-4">
        {group.layers.map((layer) => {
          const id = `ics201-map-layer-${group.id}-${layer.id}`;
          return (
            <div key={layer.id} className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id={id}
                disabled={disabled}
                checked={selectedIds.has(layer.id)}
                onCheckedChange={() => onToggle(layer)}
              />
              <Label
                htmlFor={id}
                className="tw:min-w-0 tw:text-sm tw:font-normal"
              >
                <span className="tw:min-w-0 tw:truncate">{layer.name}</span>
              </Label>
            </div>
          );
        })}
      </div>
    </CollapsibleContent>
  </Collapsible>
);

export const Ics201MapLayerPicker: React.FC<{
  groups: PickableLayerGroup[];
  isLoading: boolean;
  selectedIds: ReadonlySet<string>;
  onToggle: (layer: PickableLayer) => void;
  disabled?: boolean;
}> = ({ groups, isLoading, selectedIds, onToggle, disabled }) => {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();

  /**
   * Filtered here rather than through the catalog's own `search`, so typing
   * neither refetches nor drops the layers already ticked for the sketch.
   */
  const visible = useMemo(() => {
    if (!needle) return groups;
    return groups
      .map((group) => ({
        ...group,
        layers: group.layers.filter((layer) =>
          layer.name.toLowerCase().includes(needle)
        ),
      }))
      .filter((group) => group.layers.length > 0);
  }, [groups, needle]);

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2"
      data-testid="ics201-map-layer-picker"
    >
      <div className="tw:relative tw:min-w-0">
        <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2 tw:size-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search layers..."
          aria-label="Search layers for the sketch"
          className="tw:h-8 tw:ps-8 tw:text-xs"
          data-testid="ics201-map-layer-search"
        />
      </div>

      <div className="tw:flex tw:max-h-72 tw:min-w-0 tw:flex-col tw:gap-3 tw:overflow-y-auto tw:rounded tw:border tw:border-[var(--border-primary)] tw:p-2">
        {isLoading ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Loading layers...
          </p>
        ) : groups.length === 0 ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            No layers are available for this incident yet.
          </p>
        ) : visible.length === 0 ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            No layers match that search.
          </p>
        ) : (
          visible.map((group) => (
            <LayerCategory
              key={group.id}
              group={group}
              selectedIds={selectedIds}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Ics201MapLayerPicker;
